import moment from "moment";
import { useState } from "react";
import { Icon, UserAvatar } from "../../../components/Component";
import IgStoryPopup from "./IgStoryPopup";
import avatarPlaceholder from "../../../images/default-avatar.webp";

/**
 * IgChatBubble — render satu pesan Instagram.
 *
 * Props:
 *   item: MessageResponse {
 *     message_id, chat_id, from_user_id, from_username, text,
 *     direction ("inbound"|"outbound"), sent_via ("crm"|"instagram_app"|""),
 *     attachment_type, attachment_url,
 *     attachment_file_type, created_time, created_at
 *   }
 *   participantUsername: string — username pelanggan (untuk label avatar inbound)
 */
const IgSystemMessage = ({ text }) => (
  <div style={{ display: "flex", justifyContent: "center", padding: "4px 16px", margin: "6px 0" }}>
    <div
      style={{
        background: "#1c2b5e",
        color: "#ffffff",
        borderRadius: 20,
        padding: "6px 16px",
        fontSize: 12,
        fontStyle: "italic",
        maxWidth: "80%",
        textAlign: "center",
        lineHeight: 1.5,
      }}
    >
      {text}
    </div>
  </div>
);

const isFallbackText = (text) => /^\[Sent \d+ attachment\(s\):/.test(text || "");

// Short preview label for a quoted message that has no usable text.
// Falls back to the first item of the multi-attachment array (ATR-2744) when
// the legacy singular field is empty, e.g. a message with no text whose only
// content is 2+ attachments.
const effectiveAttachmentType = (msg) => msg.attachment_type || msg.attachments?.[0]?.type || "";

const attachmentPreviewLabel = (msg) => {
  switch (effectiveAttachmentType(msg)) {
    case "image":
      return "Photo";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    default:
      return effectiveAttachmentType(msg) ? "Attachment" : "Message";
  }
};

const attachmentIconName = (msg) => {
  switch (effectiveAttachmentType(msg)) {
    case "image":
      return "img-fill";
    case "video":
      return "video-fill";
    case "audio":
      return "mic";
    default:
      return "clip";
  }
};

/**
 * IgReplyQuote — quoted preview of the message being replied to, shown above
 * the bubble content. Uses the same reply-section classes as the WA chat
 * module (assets/css/chat.css) so both platforms look identical. Clicking it
 * scrolls to the original bubble (bubbles use message_id as DOM id).
 * `original` may be undefined when the replied-to message is outside the
 * loaded history.
 */
const IgReplyQuote = ({ replyToId, original, youChat }) => {
  const hasText = original && original.text && !isFallbackText(original.text);
  const previewText = hasText ? original.text : original ? attachmentPreviewLabel(original) : "Replied to a message";

  const scrollToOriginal = () => {
    const el = document.getElementById(replyToId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className={youChat ? "reply-section-you" : "reply-section"} onClick={scrollToOriginal}>
      {hasText || !original ? (
        <div className="reply-section-bubble">
          <p className="reply-section-text">{previewText}</p>
        </div>
      ) : (
        <div className="reply-section-media">
          <Icon name={attachmentIconName(original)} />
          <span>{previewText}</span>
        </div>
      )}
      {original?.attachment_type === "image" && original.attachment_url ? (
        <div className="reply-section-image">
          <img alt="reply" className="reply-image" src={original.attachment_url} />
        </div>
      ) : null}
    </div>
  );
};

// Per-bubble actions dropdown, same structure as the WA chat module
// (ChatPartials MeChat/YouChat): more-v trigger with a "Reply Chat" item.

const IgChatBubble = ({ item, participantUsername, participantAvatarUrl, repliedMessage, onReply }) => {
  const isSystem = item.direction === "system";
  const isOutbound = item.direction === "outbound";
  const timeLabel = item.created_time
    ? moment(item.created_time).format("HH:mm")
    : moment(item.created_at).format("HH:mm");

  const [popupOpen, setPopupOpen] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null); // { url, type, label } — image/video lightbox

  if (isSystem) return <IgSystemMessage text={item.text} />;

  // Rendered as the first child inside .chat-msg (same placement as the WA
  // chat module) so the quote sits inside the coloured bubble background.
  const replyQuote = item.reply_to_id ? (
    <IgReplyQuote replyToId={item.reply_to_id} original={repliedMessage} youChat={!isOutbound} />
  ) : null;

  const renderContent = () => {
    // Normalkan: data lama menyimpan story type di attachment_type (bukan attachment_file_type)
    const effectiveFileType =
      item.attachment_file_type ||
      (item.attachment_type === "story_reply"
        ? "story_reply"
        : item.attachment_type === "story_mention"
          ? "story_mention"
          : "");

    // Story Reply: customer membalas story bisnis
    if (effectiveFileType === "story_reply") {
      const popupLabel = isOutbound ? "Story (sent)" : "Story Reply";
      return (
        <div className="chat-msg">
          {replyQuote}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px 5px",
              borderBottom: "1px solid rgba(0,0,0,.08)",
              cursor: item.attachment_url ? "pointer" : "default",
            }}
            onClick={() => item.attachment_url && setPopupOpen(true)}
          >
            {/* Thumbnail story 32×44 */}
            <div
              style={{
                width: 32,
                height: 44,
                borderRadius: 5,
                background: "linear-gradient(160deg, #f9a825, #e91e63)",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {item.attachment_url &&
                (item.attachment_type === "video" ? (
                  <video
                    src={item.attachment_url}
                    muted
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <img
                    src={item.attachment_url}
                    alt="story"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ))}
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#666", lineHeight: 1.3 }}>
                {isOutbound ? "You replied to a story" : "Replied to your story"}
              </div>
              <div style={{ fontSize: 10, color: "#aaa" }}>
                {item.attachment_url ? "Tap to view" : "Story no longer available"}
              </div>
            </div>
          </div>
          {item.text && (
            <p className="mb-0" style={{ padding: "6px 8px 0" }}>
              {item.text}
            </p>
          )}
          <IgStoryPopup
            isOpen={popupOpen}
            onClose={() => setPopupOpen(false)}
            url={item.attachment_url}
            mediaType={item.attachment_type}
            label={popupLabel}
          />
        </div>
      );
    }

    // Story Mention: customer mention akun bisnis di story-nya
    if (effectiveFileType === "story_mention") {
      return (
        <div className="chat-msg" style={{ width: 190 }}>
          {replyQuote}
          <div style={{ padding: "6px 8px 4px", fontSize: 11, color: "#666" }}>Mentioned you in their story</div>
          <div
            style={{
              margin: "0 8px",
              borderRadius: 8,
              overflow: "hidden",
              background: "linear-gradient(160deg, #42a5f5, #26c6da)",
              height: 90,
              cursor: item.attachment_url ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => item.attachment_url && setPopupOpen(true)}
          >
            {item.attachment_url &&
              (item.attachment_type === "video" ? (
                <video
                  src={item.attachment_url}
                  muted
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <img
                  src={item.attachment_url}
                  alt="story"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ))}
          </div>
          <p style={{ fontSize: 10, color: "#aaa", textAlign: "center", padding: "4px 0 6px", margin: 0 }}>
            {item.attachment_url ? "Tap to view story" : "Story no longer available"}
          </p>
          <IgStoryPopup
            isOpen={popupOpen}
            onClose={() => setPopupOpen(false)}
            url={item.attachment_url}
            mediaType={item.attachment_type}
            label="Story Mention"
          />
        </div>
      );
    }

    // Attachments: multiple (ATR-2744) — falls back to the single legacy
    // fields below when there's no array, or the array has 1 item.
    if (item.attachments && item.attachments.length > 1) {
      return (
        <div className="chat-msg">
          {replyQuote}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {item.attachments.map((att, idx) =>
              att.type === "video" ? (
                <video
                  key={idx}
                  src={att.url}
                  controls
                  style={{ maxWidth: 160, borderRadius: 8, display: "block" }}
                />
              ) : att.type === "image" ? (
                <img
                  key={idx}
                  src={att.url}
                  alt={`attachment ${idx + 1}`}
                  style={{ maxWidth: 160, borderRadius: 8, display: "block", cursor: "pointer" }}
                  onClick={() => setMediaPreview({ url: att.url, type: "image", label: `Photo ${idx + 1}` })}
                />
              ) : (
                <a key={idx} href={att.url} target="_blank" rel="noreferrer">
                  [{att.type || "file"}] Download
                </a>
              )
            )}
          </div>
          {item.text && !isFallbackText(item.text) && <p className="mb-0 mt-1">{item.text}</p>}
        </div>
      );
    }

    // Attachment: image
    if (item.attachment_type === "image" && item.attachment_url) {
      return (
        <div className="chat-msg">
          {replyQuote}
          <img
            src={item.attachment_url}
            alt="attachment"
            style={{ maxWidth: 240, borderRadius: 8, display: "block", cursor: "pointer" }}
            onClick={() => setMediaPreview({ url: item.attachment_url, type: "image", label: "Photo" })}
          />
          {item.text && !isFallbackText(item.text) && <p className="mb-0 mt-1">{item.text}</p>}
        </div>
      );
    }

    // Attachment: video
    if (item.attachment_type === "video" && item.attachment_url) {
      return (
        <div className="chat-msg">
          {replyQuote}
          <video src={item.attachment_url} controls style={{ maxWidth: 240, borderRadius: 8, display: "block" }} />
          {item.text && !isFallbackText(item.text) && <p className="mb-0 mt-1">{item.text}</p>}
        </div>
      );
    }

    // Attachment: audio / file lainnya
    if (item.attachment_type && item.attachment_url) {
      return (
        <div className="chat-msg">
          {replyQuote}
          <a href={item.attachment_url} target="_blank" rel="noreferrer">
            [{item.attachment_type}] Download
          </a>
          {item.text && !isFallbackText(item.text) && <p className="mb-0 mt-1">{item.text}</p>}
        </div>
      );
    }

    // Pesan teks biasa
    return (
      <div className="chat-msg">
        {replyQuote}
        <p className="mb-0">{item.text}</p>
      </div>
    );
  };

  // const replyTrigger = onReply ? <IgReplyTrigger item={item} onReply={onReply} /> : null;
  const replyTrigger = onReply ? null : null;

  // Replies typed directly in the Instagram app (not through the CRM) carry
  // sent_via="instagram_app" from the adapter — flag them so agents know the
  // conversation was already handled outside the CRM. Legacy rows have no
  // sent_via and show no badge.
  const isViaInstagramApp = isOutbound && item.sent_via === "instagram_app";

  const mediaPreviewPopup = (
    <IgStoryPopup
      isOpen={!!mediaPreview}
      onClose={() => setMediaPreview(null)}
      url={mediaPreview?.url}
      mediaType={mediaPreview?.type}
      label={mediaPreview?.label}
    />
  );

  if (isOutbound) {
    return (
      <div id={item.message_id} className="chat is-me">
        <div className="chat-content">
          <div className="chat-bubbles">
            <div className="chat-bubble">
              {renderContent()}
              {replyTrigger}
            </div>
          </div>
          <ul className="chat-msg-info">
            {isViaInstagramApp && (
              <li>
                <span
                  className="badge rounded-pill"
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#e1306c",
                    border: "1px solid #e1306c",
                    background: "transparent",
                  }}
                >
                  via Instagram App
                </span>
              </li>
            )}
            <li>
              <div className="chat-msg-date">{timeLabel}</div>
            </li>
          </ul>
        </div>
        {mediaPreviewPopup}
      </div>
    );
  }

  return (
    <div id={item.message_id} className="chat is-you">
      <div className="chat-avatar">
        <UserAvatar image={participantAvatarUrl || avatarPlaceholder} className="chat-media" />
      </div>
      <div className="chat-content">
        <div className="chat-bubbles">
          <div className="chat-bubble">
            {renderContent()}
            {replyTrigger}
          </div>
        </div>
        <ul className="chat-msg-info">
          <li>
            <div className="chat-msg-date">{timeLabel}</div>
          </li>
        </ul>
        {mediaPreviewPopup}
      </div>
    </div>
  );
};

export default IgChatBubble;
